import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Switch } from "./ui/switch";
import { TrendingUp, DollarSign, Package, Receipt } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const Stat = ({ icon: Icon, label, value, testid, accent }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500"><Icon className="w-4 h-4" />{label}</div>
    <div className={`text-2xl font-extrabold mt-1 ${accent || "text-slate-900"}`}>{value}</div>
  </div>
);

const profitCls = (n) => (n < 0 ? "text-red-600" : "text-emerald-700");

const MonthlyTable = ({ rows }) => (
  <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid="profit-monthly-table">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-left"><tr><th className="p-3">Month</th><th className="p-3">Orders</th><th className="p-3">Revenue</th><th className="p-3">CJ cost</th><th className="p-3">Profit</th></tr></thead>
      <tbody>
        {rows.map((m) => (
          <tr key={m.month} className="border-t border-slate-100" data-testid={`profit-month-${m.month}`}>
            <td className="p-3 font-semibold">{new Date(`${m.month}-01T00:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" })}</td>
            <td className="p-3">{m.orders}</td><td className="p-3">{money(m.revenue)}</td><td className="p-3">{money(m.cost)}</td>
            <td className={`p-3 font-bold ${profitCls(m.profit)}`}>{money(m.profit)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const OrdersTable = ({ rows }) => (
  <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid="profit-orders-table">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-left"><tr><th className="p-3">Order</th><th className="p-3">Date</th><th className="p-3">Items</th><th className="p-3">Revenue</th><th className="p-3">CJ cost</th><th className="p-3">Profit</th><th className="p-3">Margin</th></tr></thead>
      <tbody>
        {rows.map((o) => (
          <tr key={o.order_id} className="border-t border-slate-100" data-testid={`profit-order-${o.order_id}`}>
            <td className="p-3 font-mono text-xs">{o.order_id}{o.is_test && <span className="ml-2 text-[10px] bg-slate-100 text-slate-500 rounded-full px-1.5 py-0.5">test</span>}</td>
            <td className="p-3 text-slate-500">{new Date(o.created_at).toLocaleDateString()}</td>
            <td className="p-3">{o.items}</td><td className="p-3">{money(o.revenue)}</td>
            <td className="p-3">{money(o.cost)}{o.unknown_cost_items > 0 && <span className="text-amber-600 text-xs ml-1">(+{o.unknown_cost_items} unknown)</span>}</td>
            <td className={`p-3 font-bold ${profitCls(o.profit)}`}>{money(o.profit)}</td>
            <td className="p-3">{o.margin_pct}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const ProfitPanel = () => {
  const [data, setData] = useState(null);
  const [includeTest, setIncludeTest] = useState(false);

  const load = useCallback(async () => {
    const { data: d } = await api.get("/admin/profit", { params: { include_test: includeTest } });
    setData(d);
  }, [includeTest]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-slate-500 p-4">Loading profit…</div>;
  const t = data.totals;
  return (
    <div className="space-y-5" data-testid="profit-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Profit = order total minus CJ product cost. Excludes CJ shipping and Stripe fees.</p>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <Switch checked={includeTest} onCheckedChange={setIncludeTest} data-testid="profit-include-test-toggle" /> Include test orders
        </label>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Receipt} label="Paid orders" value={t.orders} testid="profit-total-orders" />
        <Stat icon={DollarSign} label="Revenue" value={money(t.revenue)} testid="profit-total-revenue" />
        <Stat icon={Package} label="CJ cost" value={money(t.cost)} testid="profit-total-cost" />
        <Stat icon={TrendingUp} label="Profit" value={money(t.profit)} testid="profit-total-profit" accent={profitCls(t.profit)} />
      </div>
      {t.orders === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-500" data-testid="profit-empty">
          No paid customer orders yet. Turn on "Include test orders" to preview the numbers.
        </div>
      ) : (
        <>
          <div><h3 className="font-bold text-slate-900 mb-2">By month</h3><MonthlyTable rows={data.monthly} /></div>
          <div><h3 className="font-bold text-slate-900 mb-2">By order</h3><OrdersTable rows={data.orders} /></div>
        </>
      )}
    </div>
  );
};
